import React, { useRef } from "react";

import cc from "classcat";

import styles from "./SegmentedControl.module.scss";

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: string;
}

export interface SegmentedControlProps<T extends string = string> {
  options: SegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  testId?: string;
  /** Required — a radiogroup needs an accessible name. */
  ariaLabel: string;
}

const SegmentedControl = <T extends string = string>({
  options,
  value,
  onChange,
  testId,
  ariaLabel,
}: SegmentedControlProps<T>) => {
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const moveSelection = (fromIndex: number, delta: number) => {
    const nextIndex = (fromIndex + delta + options.length) % options.length;
    onChange(options[nextIndex].value);
    optionRefs.current[nextIndex]?.focus();
  };

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    index: number
  ) => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        moveSelection(index, 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        moveSelection(index, -1);
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={styles.group}
      role="radiogroup"
      aria-label={ariaLabel}
      data-testid={testId}
    >
      {options.map((option, index) => {
        const isSelected = option.value === value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              optionRefs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            tabIndex={isSelected ? 0 : -1}
            className={cc([styles.option, { [styles.selected]: isSelected }])}
            onClick={(event) => {
              onChange(option.value);
              // Safari doesn't focus buttons on click; force it so the
              // roving tabindex stays in sync with real DOM focus.
              event.currentTarget.focus();
            }}
            onKeyDown={(event) => handleKeyDown(event, index)}
            data-testid={
              testId ? `${testId}-option-${option.value}` : undefined
            }
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default SegmentedControl;
